import "./instrument";
// Before the components, whose CSS modules override Mantine's styles with selectors of the same specificity
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import React, { startTransition, Suspense } from "react";
import { createRoot } from "react-dom/client";
import {
  createTheme,
  CSSVariablesResolver,
  MantineProvider,
  v8CssVariablesResolver,
} from "@mantine/core";
import { MatomoProvider, createInstance } from "@datapunt/matomo-tracker-react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Notifications } from "@mantine/notifications";
import { App } from "./App";
import { LoadingScreen } from "./LoadingScreen";
import { ErrorBoundary } from "./ErrorBoundary";
import { SharedLocationRedirect } from "./SharedLocationRedirect";
import "./index.css";
import "./utils/i18n/i18n";

const BASE_API_URL = import.meta.env.VITE_BASE_API_URL;

const matomoInstance = createInstance({
  urlBase: BASE_API_URL,
  siteId: 3,
  trackerUrl: "https://analytics.vecturagames.com/matomo.php",
  srcUrl: "https://analytics.vecturagames.com/matomo.js",
  linkTracking: false,
  configurations: {
    disableCookies: true,
  },
});

const trackbookTheme = createTheme({
  fontSizes: {
    xs: "13px",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.1)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.2)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.2), 0 2px 4px -2px rgb(0 0 0 / 0.2)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.2), 0 8px 10px -6px rgb(0 0 0 / 0.2)",
  },
  // Mantine 8's look, which Mantine 9 changed: a 4px radius and medium text of weight 500
  defaultRadius: "sm",
  fontWeights: { medium: "500" },
  cursorType: "pointer",
  primaryColor: "violet",
});

const container = document.getElementById("root");
const root = createRoot(container!);

/**
 * Mantine 8's variables (its light variants have transparent colors, Mantine 9's are solid), with dimmed texts
 * (hints, the stats' labels) that have a contrast of 4.5:1 (WCAG AA) on the panels: Mantine's gray 6 is 3.3:1 on
 * white, its dark 2 4.0:1 on the dark panel
 */
const cssVariablesResolver: CSSVariablesResolver = (theme) => {
  const v8 = v8CssVariablesResolver(theme);
  return {
    ...v8,
    light: { ...v8.light, "--mantine-color-dimmed": "#6c757d" },
    dark: { ...v8.dark, "--mantine-color-dimmed": "#979797" },
  };
};

// Rendered as a transition, so React renders the app in short slices instead of long tasks that block the page
startTransition(() =>
  root.render(
    <React.StrictMode>
      <MatomoProvider value={matomoInstance}>
        <MantineProvider
          defaultColorScheme="auto"
          theme={trackbookTheme}
          cssVariablesResolver={cssVariablesResolver}
        >
          <Suspense fallback={<LoadingScreen />}>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ErrorBoundary>
                      {/* Hovering a notification pauses only its own timer, like Mantine 8 */}
                      <Notifications pauseResetOnHover="notification" />
                      <App />
                    </ErrorBoundary>
                  }
                />
                <Route path="/loc" element={<SharedLocationRedirect />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </Suspense>
        </MantineProvider>
      </MatomoProvider>
    </React.StrictMode>
  )
);
